// @ts-nocheck
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Table,
  Form,
  Button,
  InputGroup,
  Container,
} from "react-bootstrap";
import PageLoading from "components/PageLoading";
import { FaSearch, FaEdit, FaLock, FaLockOpen } from "react-icons/fa";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import ProfileFilter from "../../components/ProfileFilter/ProfileFilter";
import NotificationToast from "../../components/NotificationToast/NotificationToast";
import { getAllUsuarios, toggleUsuarioAtivo } from "../../services";
import { formatCnpj } from "../../utils";
import { PATHS } from "../../routes/paths";
import PaginationControls from "../Documentation/Analysis/ComponentsAnalysis/PaginationControls/PaginationControls";
import "./ManageUsers.css";

export interface ManageUser {
  id: number;
  name: string;
  email: string;
  cnpj: string;
  sigla: string;
  idPerfil?: number;
  profile: string;
  blocked: boolean;
}

const ManageUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [users, setUsers] = useState<ManageUser[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [selectedUserIndex, setSelectedUserIndex] = useState<number | null>(null);

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: string }>({
    key: "name",
    direction: "asc",
  });
  const [loading, setLoading] = useState(true);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isBulkAction, setIsBulkAction] = useState(false);
  const [bulkActionMode, setBulkActionMode] = useState("block");
  const [processing, setProcessing] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchUsers = async () => {
    try {
      const response = await getAllUsuarios();
      if (!response.data || !response.data.usuarios) {
        throw new Error("❌ Campo 'usuarios' ausente na resposta.");
      }
      const mappedUsers = response.data.usuarios.map((u) => ({
        id: u.id,
        name: u.nome,
        email: u.email,
        cnpj: u.cnpj || "-",
        sigla: u.sigla || "-",
        idPerfil: u.idPerfil,
        profile: u.perfil,
        blocked: !u.ativo,
      }));
      setUsers(mappedUsers);
    } catch (error) {
      console.error("❌ Erro ao buscar usuários:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.refresh) {
      fetchUsers();
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return users.filter(
      (user) =>
        (selectedProfiles.length === 0 ||
          selectedProfiles.includes(user.profile)) &&
        (user.name.toLowerCase().includes(term) ||
          user.email.toLowerCase().includes(term) ||
          user.profile.toLowerCase().includes(term)),
    );
  }, [users, selectedProfiles, searchTerm]);

  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return filteredUsers;
    const key = sortConfig.key as keyof ManageUser;
    return [...filteredUsers].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      if (aVal < bVal)
        return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal)
        return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredUsers, sortConfig]);

  const indexOfLastUser = currentPage * itemsPerPage;
  const indexOfFirstUser = indexOfLastUser - itemsPerPage;
  const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser);

  const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
  const startResult = (currentPage - 1) * itemsPerPage + 1;
  const endResult = Math.min(currentPage * itemsPerPage, sortedUsers.length);

  const currentPageIds = useMemo(
    () => currentUsers.map((u) => u.id),
    [currentUsers],
  );
  const isSelected = (id) => selectedIds.has(String(id));

  const toggleSelect = (id) => {
    const sid = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(sid) ? next.delete(sid) : next.add(sid);
      return next;
    });
  };

  const allSelectedOnPage = useMemo(
    () =>
      currentPageIds.length > 0 &&
      currentPageIds.every((id) => selectedIds.has(String(id))),
    [currentPageIds, selectedIds],
  );

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage)
        currentPageIds.forEach((id) => next.delete(String(id)));
      else currentPageIds.forEach((id) => next.add(String(id)));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectedUsers = useMemo(
    () => users.filter((u) => selectedIds.has(String(u.id))),
    [users, selectedIds],
  );
  const canBlockCount = selectedUsers.filter((u) => !u.blocked).length;
  const canUnblockCount = selectedUsers.filter((u) => u.blocked).length;

  const handleToggleModal = (userId) => {
    const index = users.findIndex((u) => u.id === userId);
    const user = users[index];
    const message = user.blocked
      ? "Tem certeza que deseja desbloquear esse usuário? Ele começará a ter acesso ao sistema."
      : "Tem certeza que deseja bloquear esse usuário? Ele não terá mais acesso ao sistema.";
    setModalMessage(message);
    setSelectedUserIndex(index);
    setIsBulkAction(false);
    setShowModal(true);
  };

  const openBulkModal = (mode) => {
    const count = mode === "block" ? canBlockCount : canUnblockCount;

    if (count === 0) {
      setToastMessage(
        mode === "block"
          ? "Nenhum dos selecionados precisa ser bloqueado."
          : "Nenhum dos selecionados precisa ser desbloqueado.",
      );
      setToastType(mode === "block" ? "error" : "success");
      setShowToast(true);
      return;
    }

    setBulkActionMode(mode);
    setModalMessage(
      mode === "block"
        ? `Tem certeza que deseja bloquear ${count} usuário(s) selecionado(s)?`
        : `Tem certeza que deseja desbloquear ${count} usuário(s) selecionado(s)?`,
    );
    setSelectedUserIndex(null);
    setIsBulkAction(true);
    setShowModal(true);
  };

  const handleConfirmToggle = async () => {
    try {
      if (isBulkAction) {
        setProcessing(true);

        const idsSelecionados = Array.from(selectedIds);
        const wantAtivo = bulkActionMode === "unblock";

        const idsParaProcessar = users
          .filter((u) => idsSelecionados.includes(String(u.id)))
          .filter((u) => (bulkActionMode === "block" ? !u.blocked : u.blocked))
          .map((u) => String(u.id));

        const okIds = [];
        const failIds = [];

        for (const id of idsParaProcessar) {
          try {
            await toggleUsuarioAtivo({ id, ativo: wantAtivo });
            okIds.push(id);
          } catch (e) {
            console.error("Falha ao aplicar ação no id:", id, e);
            failIds.push(id);
          }
        }

        const okSet = new Set(okIds.map(String));
        if (okSet.size > 0) {
          setUsers((prev) =>
            prev.map((u) =>
              okSet.has(String(u.id))
                ? { ...u, blocked: bulkActionMode === "block" }
                : u,
            ),
          );
        }

        setSelectedIds(new Set(failIds.map(String)));

        const falhas = failIds.length;
        const sucesso = okIds.length;

        if (bulkActionMode === "block") {
          setToastMessage(
            falhas === 0
              ? `Usuário(s) bloqueado(s): ${sucesso}.`
              : `Bloqueio parcial: ${sucesso} sucesso(s), ${falhas} falha(s).`,
          );
          setToastType("error");
        } else {
          setToastMessage(
            falhas === 0
              ? `Usuário(s) desbloqueado(s): ${sucesso}.`
              : `Desbloqueio parcial: ${sucesso} sucesso(s), ${falhas} falha(s).`,
          );
          setToastType("success");
        }

        setIsBulkAction(false);
        setSelectedUserIndex(null);
      } else {
        const user = users[selectedUserIndex];

        const willBlock = !user.blocked;
        const willBeActive = !willBlock;

        await toggleUsuarioAtivo({ id: user.id, ativo: willBeActive });

        setUsers((prev) => {
          const next = [...prev];
          next[selectedUserIndex] = {
            ...next[selectedUserIndex],
            blocked: willBlock,
          };
          return next;
        });

        if (willBlock) {
          setToastMessage("Usuário bloqueado!");
          setToastType("error");
        } else {
          setToastMessage("Usuário desbloqueado!");
          setToastType("success");
        }
      }
    } catch (error) {
      console.error("❌ Erro ao atualizar status do(s) usuário(s):", error);
      setToastMessage("Erro ao atualizar status do usuário.");
      setToastType("error");
    } finally {
      setProcessing(false);
      setShowModal(false);
      setShowToast(true);
    }
  };

  return (
    <Container className="manage-users-container">
      <h1 className="manage-users-title">LISTA DE USUÁRIOS</h1>

      {loading ? (
        <PageLoading />
      ) : (
        <>
          <div className="filters-actions-container">
            <ProfileFilter
              selectedProfiles={selectedProfiles}
              handleProfileFilter={(p) =>
                setSelectedProfiles((prev) =>
                  prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
                )
              }
            />

            <div className="search-container">
              <InputGroup>
                <InputGroup.Text className="search-icon">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Pesquisar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>

              <Button
                variant="primary"
                className="add-user-button"
                onClick={() => navigate(PATHS.MANAGE_USERS_ADD)}
              >
                + Adicionar Usuário
              </Button>
            </div>
          </div>

          <div className="selection-bar">
            <span className="count">Selecionados: {selectedIds.size}</span>
            {selectedIds.size > 0 && (
              <>
                <button
                  className="clear-btn"
                  onClick={clearSelection}
                  disabled={processing}
                >
                  Limpar seleção
                </button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => openBulkModal("block")}
                  disabled={processing || canBlockCount === 0}
                  className="me-2"
                >
                  <FaLock className="me-2" />
                  Bloquear selecionados
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => openBulkModal("unblock")}
                  disabled={processing || canUnblockCount === 0}
                >
                  <FaLockOpen className="me-2" />
                  Desbloquear selecionados
                </Button>
              </>
            )}
          </div>

          <Table bordered hover responsive className="users-table">
            <thead>
              <tr>
                <th className="checkbox-header">
                  <Form.Check
                    type="checkbox"
                    checked={allSelectedOnPage}
                    onChange={toggleSelectAllOnPage}
                    disabled={processing}
                    title={
                      allSelectedOnPage
                        ? "Desmarcar todos da página"
                        : "Selecionar todos da página"
                    }
                  />
                </th>

                <th
                  onClick={() =>
                    setSortConfig((s) => ({
                      key: "name",
                      direction: s.direction === "asc" ? "desc" : "asc",
                    }))
                  }
                  className="sortable-header"
                >
                  NOME DO USUÁRIO{" "}
                  {sortConfig.key === "name"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : "↕"}
                </th>
                <th>CNPJ</th>
                <th
                  onClick={() =>
                    setSortConfig((s) => ({
                      key: "sigla",
                      direction: s.direction === "asc" ? "desc" : "asc",
                    }))
                  }
                  className="sortable-header"
                >
                  SIGLA{" "}
                  {sortConfig.key === "sigla"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : "↕"}
                </th>
                <th
                  onClick={() =>
                    setSortConfig((s) => ({
                      key: "email",
                      direction: s.direction === "asc" ? "desc" : "asc",
                    }))
                  }
                  className="sortable-header"
                >
                  E-MAIL DO USUÁRIO{" "}
                  {sortConfig.key === "email"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : "↕"}
                </th>
                <th>PERFIL</th>
                <th className="text-center">AÇÕES</th>
              </tr>
            </thead>

            <tbody>
              {currentUsers.map((user) => (
                <tr key={user.id}>
                  <td className="checkbox-cell">
                    <Form.Check
                      type="checkbox"
                      checked={isSelected(user.id)}
                      onChange={() => toggleSelect(user.id)}
                      disabled={processing}
                      title={`Selecionar ${user.name}`}
                    />
                  </td>

                  <td>{user.name}</td>
                  <td>{formatCnpj(user.cnpj)}</td>
                  <td>{user.sigla}</td>
                  <td>{user.email}</td>
                  <td>{user.profile}</td>

                  <td className="actions-cell">
                    <div className="actions-buttons">
                      {user.profile === "Administrador" ||
                        user.profile === "Analista" ? (
                        <Button
                          size="sm"
                          className="btn-edit-user me-2"
                          onClick={() =>
                            navigate(
                              PATHS.MANAGE_USERS_EDIT.replace(
                                ":userId",
                                String(user.id),
                              ),
                            )
                          }
                          disabled={processing}
                        >
                          <FaEdit />
                        </Button>
                      ) : (
                        <div className="btn-placeholder me-2" />
                      )}
                      <Button
                        variant={user.blocked ? "danger" : "success"}
                        size="sm"
                        onClick={() => handleToggleModal(user.id)}
                        disabled={processing}
                      >
                        {user.blocked ? <FaLock /> : <FaLockOpen />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="pagination-container">
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              startResult={startResult}
              endResult={endResult}
              totalItems={sortedUsers.length}
            />
          </div>

          <ConfirmationModal
            isOpen={showModal}
            title="Confirmação"
            message={modalMessage}
            onConfirm={handleConfirmToggle}
            onCancel={() => setShowModal(false)}
          />

          <NotificationToast
            show={showToast}
            onClose={() => setShowToast(false)}
            message={toastMessage}
            type={toastType}
          />
        </>
      )}
    </Container>
  );
};

export default ManageUsers;
